import { useState } from "react";
import { useParams } from "@tanstack/react-router";
import { QRCodeSVG } from "qrcode.react";
import { trpc } from "@/lib/trpc";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  Building2,
  Home,
  User,
} from "lucide-react";
import { toast } from "sonner";

const categoryLabels = {
  TEMPORARY: "Visita temporal",
  PERMANENT: "Acceso permanente",
  SERVICE: "Proveedor",
};

function formatDate(date: Date | string) {
  return new Date(date).toLocaleDateString("es-AR", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function GuestInvitationPage() {
  const { token } = useParams({ from: "/invite/$token" });
  const utils = trpc.useUtils();

  const [formData, setFormData] = useState({
    guestName: "",
    guestEmail: "",
    guestDni: "",
  });

  const {
    data: invitation,
    isLoading,
    error,
  } = trpc.invitation.getByToken.useQuery({ token });

  const completeInvitation = trpc.invitation.complete.useMutation({
    onSuccess: () => {
      utils.invitation.getByToken.invalidate({ token });
      toast.success("¡Invitación completada!");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    completeInvitation.mutate({
      token,
      guestName: formData.guestName,
      guestEmail: formData.guestEmail,
      guestDni: formData.guestDni,
    });
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !invitation) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <XCircle className="mx-auto h-12 w-12 text-destructive" />
            <p className="mt-4 text-lg font-medium">Invitación no encontrada</p>
            <p className="mt-2 text-muted-foreground">
              Este enlace de invitación puede ser inválido o haber expirado.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invitation is revoked or expired
  if (invitation.status === "REVOKED" || invitation.status === "EXPIRED") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <XCircle className="mx-auto h-12 w-12 text-destructive" />
            <p className="mt-4 text-lg font-medium">
              {invitation.status === "REVOKED"
                ? "Invitación revocada"
                : "Invitación vencida"}
            </p>
            <p className="mt-2 text-muted-foreground">
              {invitation.status === "REVOKED"
                ? "Esta invitación fue cancelada por el anfitrión."
                : "Esta invitación ya no es válida."}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invitation is already completed (ACTIVE)
  if (invitation.status === "ACTIVE") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <CardTitle>Tu invitación</CardTitle>
            <CardDescription>
              Mostrá este código QR en el acceso
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            <div className="flex flex-col items-center gap-4 rounded-xl border bg-white p-6">
              <QRCodeSVG
                value={`${window.location.origin}/invite/${token}`}
                size={200}
                level="M"
                includeMargin
              />
              <p className="text-sm font-medium">{invitation.guestName}</p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span>{invitation.community.name}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Home className="h-4 w-4 text-muted-foreground" />
                <span>
                  {invitation.unit.blockSection
                    ? `${invitation.unit.blockSection} - ${invitation.unit.unitNumber}`
                    : invitation.unit.unitNumber}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <User className="h-4 w-4 text-muted-foreground" />
                <span>Anfitrión: {invitation.hostName || "Desconocido"}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span>
                  {invitation.category === "PERMANENT"
                    ? `Válida desde ${formatDate(invitation.validFrom)}`
                    : `${formatDate(invitation.validFrom)} - ${invitation.validUntil ? formatDate(invitation.validUntil) : "Sin vencimiento"}`}
                </span>
              </div>
            </div>

            <Alert>
              <AlertDescription>
                Hacé una captura de pantalla o guardá el código QR para mostrar
                en el acceso.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invitation is PENDING - show form
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle>Completá tu invitación</CardTitle>
          <CardDescription>
            Fuiste invitado/a a {invitation.community.name}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="space-y-3 rounded-xl border p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Tipo</span>
              <Badge variant="secondary">
                {categoryLabels[invitation.category]}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Unidad</span>
              <span className="text-sm font-medium">
                {invitation.unit.blockSection
                  ? `${invitation.unit.blockSection} - ${invitation.unit.unitNumber}`
                  : invitation.unit.unitNumber}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Anfitrión</span>
              <span className="text-sm font-medium">
                {invitation.hostName || "Desconocido"}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Vigencia</span>
              <span className="text-sm font-medium">
                {invitation.category === "PERMANENT"
                  ? `Desde ${formatDate(invitation.validFrom)}`
                  : `${formatDate(invitation.validFrom)} - ${invitation.validUntil ? formatDate(invitation.validUntil) : "Sin vencimiento"}`}
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="guestName">Nombre completo *</Label>
              <Input
                id="guestName"
                value={formData.guestName}
                onChange={(e) =>
                  setFormData({ ...formData, guestName: e.target.value })
                }
                placeholder="Ingresá tu nombre completo"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="guestEmail">Email *</Label>
              <Input
                id="guestEmail"
                type="email"
                value={formData.guestEmail}
                onChange={(e) =>
                  setFormData({ ...formData, guestEmail: e.target.value })
                }
                placeholder="Ingresá tu email"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="guestDni">DNI / Pasaporte *</Label>
              <Input
                id="guestDni"
                value={formData.guestDni}
                onChange={(e) =>
                  setFormData({ ...formData, guestDni: e.target.value })
                }
                placeholder="Ingresá tu número de documento"
                required
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={
                completeInvitation.isPending ||
                !formData.guestName ||
                !formData.guestEmail ||
                !formData.guestDni
              }
            >
              {completeInvitation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Completando...
                </>
              ) : (
                "Completar y obtener QR"
              )}
            </Button>
          </form>

          <p className="text-center text-xs text-muted-foreground">
            Al completar este formulario, aceptás compartir tu información con
            el barrio para fines de acceso.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
